import React, { useState, useRef } from 'react';

const BulkUploadModal = ({ assignments, onClose, onUpload }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileWordCounts, setFileWordCounts] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const docxFiles = files.filter(file => file.name.toLowerCase().endsWith('.docx'));

    if (docxFiles.length !== files.length) {
      alert('Only DOCX files are allowed. Non-DOCX files have been filtered out.');
    }

    setSelectedFiles(prev => [...prev, ...docxFiles]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      const removedFile = newFiles.splice(index, 1)[0];

      // Clean up word count state for removed file
      setFileWordCounts(counts => {
        const newCounts = { ...counts };
        delete newCounts[removedFile.name];
        return newCounts;
      });

      return newFiles;
    });
  };

  const handleWordCountChange = (fileName, value) => {
    setFileWordCounts(prev => ({
      ...prev,
      [fileName]: value
    }));
  };

  const allFilesHaveWordCount = selectedFiles.every(file => fileWordCounts[file.name] && Number(fileWordCounts[file.name]) > 0);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    if (!allFilesHaveWordCount) {
      alert('Please provide a valid word count for all selected files');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const result = await onUpload(selectedFiles, fileWordCounts, (progress) => {
        setUploadProgress(progress);
      });
      setUploadResult(result);
    } catch (error) {
      setUploadResult({
        success: false,
        errors: ['Upload failed: ' + error.message]
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      onClose();
    }
  };

  const handleRetry = () => {
    // Option 1: Keep failed files, remove successful ones
    if (uploadResult.failed_uploads && uploadResult.failed_uploads.length > 0) {
      const failedFileNames = uploadResult.failed_uploads.map(f => f.filename || f.article_id + '.docx');
      setSelectedFiles(prev => prev.filter(file => failedFileNames.includes(file.name)));
    }
    setUploadResult(null);
    setUploadProgress(0);
  };

  const handleResetAndRetry = () => {
    // Option 2: Clear everything and start fresh
    setSelectedFiles([]);
    setFileWordCounts({});
    setUploadResult(null);
    setUploadProgress(0);
  };

  const getAssignedArticles = () => {
    return assignments.filter(a => a.status === 'assigned').map(a => a.articleId);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">Bulk Upload DOCX Files</h3>
            <button
              onClick={handleClose}
              disabled={uploading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              ✕
            </button>
          </div>

          {/* Instructions */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">📋 Upload Instructions:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Files must be named exactly as: <code className="bg-blue-100 px-1 rounded">articleId.docx</code></li>
              <li>• Only DOCX format is accepted</li>
              <li>• You can only upload files for articles assigned to you</li>
              <li>• Assigned articles: {getAssignedArticles().join(', ')}</li>
            </ul>
          </div>

          {/* File Drop Zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'
              } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".docx"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <div className="space-y-2">
              <div className="text-4xl">📁</div>
              <div className="text-lg font-medium text-gray-900">
                Drop DOCX files here or click to browse
              </div>
              <div className="text-sm text-gray-500">
                Multiple files supported
              </div>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-900 mb-3">
                Selected Files ({selectedFiles.length})
              </h4>
              <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Size
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Word Count
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedFiles.map((file, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <span className="text-xl">📄</span>
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={file.name}>
                              {file.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="1"
                            required
                            placeholder="Required"
                            className={`text-sm rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 w-24 ${!fileWordCounts[file.name] ? 'border-red-300' : 'border-gray-300'}`}
                            value={fileWordCounts[file.name] || ''}
                            onChange={(e) => handleWordCountChange(file.name, e.target.value)}
                            disabled={uploading}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => removeFile(index)}
                            disabled={uploading}
                            className="text-red-500 hover:text-red-700 disabled:opacity-50 p-1"
                            title="Remove file"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="mt-6">
              <div className={`p-4 rounded-lg ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <h4 className={`font-medium mb-2 ${uploadResult.success ? 'text-green-900' : 'text-red-900'}`}>
                  {uploadResult.success ? '✅ Uploaded successfully! Email notification sent to coordinators.' : '❌ Upload Failed'}
                </h4>

                {uploadResult.successful_uploads && uploadResult.successful_uploads.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm text-green-800 font-medium">Successful uploads:</p>
                    <ul className="text-sm text-green-700 list-disc list-inside">
                      {uploadResult.successful_uploads.map((upload, index) => (
                        <li key={index}>{upload.filename}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {uploadResult.failed_uploads && uploadResult.failed_uploads.length > 0 && (
                  <div>
                    <p className="text-sm text-red-800 font-medium">Failed uploads:</p>
                    {uploadResult.failed_uploads.map((failure, index) => (
                      <div key={index} className="text-sm text-red-700 ml-4">
                        <strong>{failure.filename}:</strong>
                        <ul className="list-disc list-inside ml-4">
                          {failure.errors.map((error, errorIndex) => (
                            <li key={errorIndex}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {uploadResult.errors && (
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              {uploadResult ? 'Close' : 'Cancel'}
            </button>

            {uploadResult && uploadResult.failed_uploads && uploadResult.failed_uploads.length > 0 && (
              <button
                onClick={handleRetry}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md"
              >
                🔄 Retry Failed ({uploadResult.failed_uploads.length})
              </button>
            )}

            {uploadResult && (
              <button
                onClick={handleResetAndRetry}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                ➕ Upload More
              </button>
            )}

            {!uploadResult && (
              <div className="flex flex-col items-end">
                <button
                  onClick={handleUpload}
                  disabled={uploading || selectedFiles.length === 0 || !allFilesHaveWordCount}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md
                  ${uploading || selectedFiles.length === 0 || !allFilesHaveWordCount
                      ? 'bg-indigo-400 cursor-not-allowed opacity-50'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  title={(!allFilesHaveWordCount && selectedFiles.length > 0) ? "All files must have a valid word count" : ""}
                >
                  {uploading ? `Uploading... ${uploadProgress || 0}%` : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
                </button>
                {uploading && (
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress || 0}%` }}></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
